import { flatten } from "flat";
import { z } from "zod";

import { getSuggestionLabel } from "@/pages/Private/pages/List/components/CampaignOverview/utils/suggestions.utils";
import { getSuggestions } from "@/pages/Private/pages/List/utils/column-utils";

import { FieldMappingAiConfig } from "../../schema/hubspot";
import {
	AiConfigFormValues,
	AiConfigLabels,
	AiConfigProspectData,
	AiConfigProspectOption,
	AiConfigTargetField,
	AiVariableOption,
} from "./aiConfig.types";

const composeProspectName = (item: AiConfigProspectData): string => {
	const firstLast = [item.firstName, item.lastName]
		.map((part) => part?.trim())
		.filter((part): part is string => Boolean(part))
		.join(" ");

	const candidate = firstLast || item.name?.trim() || item.email?.trim() || "";

	return candidate || `Prospect ${item.id}`;
};

const composeProspectTitle = (item: AiConfigProspectData): string => {
	const baseName = composeProspectName(item);
	const orgName = item.organization?.name?.trim();

	return orgName ? `${baseName} — ${orgName}` : baseName;
};

export const buildAiProspectOptions = (items: AiConfigProspectData[]): AiConfigProspectOption[] => {
	const seen = new Set<number>();
	const options: AiConfigProspectOption[] = [];

	for (const item of items) {
		if (seen.has(item.id)) {
			continue;
		}

		seen.add(item.id);
		options.push({ id: item.id, title: composeProspectTitle(item), data: item });
	}

	return options;
};

export const ALLOWED_VALUES_VARIABLE_KEY = "allowedValues";

const getAiVariableGroup = (key: string) => {
	if (key === ALLOWED_VALUES_VARIABLE_KEY) {
		return "Target field";
	}

	if (key.startsWith("prospect_organization_")) {
		return "Organization";
	}

	if (key.startsWith("prospect_")) {
		return "Prospect";
	}

	if (key.startsWith("column_")) {
		return "Custom field";
	}

	return "Other";
};

// In the CRM field-mapping context only prospect/organization variables are resolvable from
// the selected prospect record, so list-context keys (column_*, liProfileEntryResult_*, …) are excluded.
const isProspectScopedVariable = (key: string) => key.startsWith("prospect_");

export const AI_VARIABLES: AiVariableOption[] = getSuggestions(undefined)
	.filter(isProspectScopedVariable)
	.map((key) => {
		const label = getSuggestionLabel(key);

		if (!label) {
			return null;
		}

		return {
			key,
			token: `{{${key}}}`,
			label,
			group: getAiVariableGroup(key),
		} satisfies AiVariableOption;
	})
	.filter((option): option is AiVariableOption => Boolean(option));

export const ALLOWED_VALUES_VARIABLE: AiVariableOption = {
	key: ALLOWED_VALUES_VARIABLE_KEY,
	token: `{{${ALLOWED_VALUES_VARIABLE_KEY}}}`,
	label: "Allowed values",
	group: getAiVariableGroup(ALLOWED_VALUES_VARIABLE_KEY),
};

const hasAllowedValues = (targetField: AiConfigTargetField | undefined) =>
	(targetField?.options?.length ?? 0) > 0;

export const getAiVariableOptions = (
	targetField: AiConfigTargetField | undefined
): AiVariableOption[] =>
	hasAllowedValues(targetField) ? [...AI_VARIABLES, ALLOWED_VALUES_VARIABLE] : AI_VARIABLES;

// Generic so callers can pass richer prospect objects (e.g. ContactProspect with organization
// industry/country) without losing those fields. Only keys that match the well-known
// AI_VARIABLES are kept.
export const buildAiVariablesFromProspect = <T extends AiConfigProspectData>(
	prospect: T
): Record<string, string> => {
	const flat = flatten<{ prospect: T }, Record<string, unknown>>({ prospect }, { delimiter: "_" });
	const result: Record<string, string> = {};

	for (const variable of AI_VARIABLES) {
		const raw = flat[variable.key];

		if (raw == null || typeof raw === "object") {
			continue;
		}

		result[variable.key] = String(raw);
	}

	return result;
};

export const buildAiVariablesFromAllowedValues = (
	allowedValues: string[] | undefined
): Record<string, string> => {
	if (!allowedValues?.length) {
		return {};
	}

	return { [ALLOWED_VALUES_VARIABLE_KEY]: allowedValues.join(", ") };
};

const hasType = (field: AiConfigTargetField | undefined, values: string[]) => {
	const type = `${field?.type ?? ""} ${field?.fieldType ?? ""}`.toLowerCase();

	return values.some((value) => type.includes(value));
};

export const isDropdownField = (field: AiConfigTargetField | undefined) =>
	Boolean(field?.options?.length) || hasType(field, ["picklist", "dropdown", "select", "enum"]);

export const isTextLikeField = (field: AiConfigTargetField | undefined) =>
	!field?.isReadOnly &&
	!field?.isCalculated &&
	hasType(field, ["text", "textarea", "string", "varchar", "multiline"]);

export const buildDefaultPrompt = (field: AiConfigTargetField | undefined, template: string) => {
	const targetLabel = field?.label || field?.name || "";

	// `{{allowedValues}}` is left in the template intentionally; it is resolved as a live variable
	// at test time (and editable in the variables panel) instead of being baked in here.
	return template.replace("{{target}}", targetLabel);
};

export const toAiConfigPayload = (values: AiConfigFormValues): FieldMappingAiConfig => ({
	prompt: values.prompt.trim(),
	fallbackValue: values.fallbackValue?.trim() || undefined,
	syncReasoningTargetField: values.syncReasoning ? values.syncReasoningTargetField : undefined,
	syncSourceTargetField: values.syncSource ? values.syncSourceTargetField : undefined,
});

export const resolveAiVariableToken = (tokenOrKey: string) => {
	const token = tokenOrKey.trim();

	if (token.startsWith("{{") && token.endsWith("}}")) {
		return token;
	}

	return `{{${token}}}`;
};

export const findAiVariableOption = (tokenOrKey: string) => {
	const normalizedToken = resolveAiVariableToken(tokenOrKey);
	const trimmedKey = tokenOrKey.trim();

	const knownOptions = [...AI_VARIABLES, ALLOWED_VALUES_VARIABLE];

	return knownOptions.find(
		(option) => option.token === normalizedToken || option.key === trimmedKey
	);
};

export const getAiVariableDisplayLabel = (tokenOrKey: string) => {
	const option = findAiVariableOption(tokenOrKey);

	if (option) {
		return option.label;
	}

	return tokenOrKey.replace(/^[{]{2}|[}]{2}$/g, "");
};

export const applyAiVariablesToPrompt = (
	prompt: string,
	variables: Record<string, string | undefined>
) =>
	prompt.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, rawKey: string) => {
		const key = rawKey.trim();

		return variables[key] ?? variables[resolveAiVariableToken(key)] ?? "";
	});

export const createAiConfigSchema = (
	labels: AiConfigLabels,
	allowedValues: string[],
	textFieldNames: string[]
) =>
	z
		.object({
			prompt: z.string().trim().min(1, labels.requiredPrompt),
			fallbackValue: z.string().optional(),
			syncReasoning: z.boolean(),
			syncReasoningTargetField: z.string().optional(),
			syncSource: z.boolean(),
			syncSourceTargetField: z.string().optional(),
		})
		.superRefine((values, ctx) => {
			const fallback = values.fallbackValue?.trim();

			if (fallback && allowedValues.length && !allowedValues.includes(fallback)) {
				ctx.addIssue({ code: "custom", message: labels.invalidFallback, path: ["fallbackValue"] });
			}

			if (
				values.syncReasoning &&
				(!values.syncReasoningTargetField ||
					!textFieldNames.includes(values.syncReasoningTargetField))
			) {
				ctx.addIssue({
					code: "custom",
					message: labels.requiredSecondaryField,
					path: ["syncReasoningTargetField"],
				});
			}

			if (
				values.syncSource &&
				(!values.syncSourceTargetField || !textFieldNames.includes(values.syncSourceTargetField))
			) {
				ctx.addIssue({
					code: "custom",
					message: labels.requiredSecondaryField,
					path: ["syncSourceTargetField"],
				});
			}
		});
